import {
  Box,
  FormControl,
  FormErrorMessage,
  Text,
  VStack,
} from "@chakra-ui/react";
import { debounce } from "lodash";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { NodeProps } from "reactflow";
import { FLOW_ACTIONS } from "../../../../../../common/constants/campaign";
import { FlowSalesforceActionOptions } from "../../../../../../common/types/campaign";
import { ActionNodeArgs } from "../../../../../../common/types/flow";
import { SOURCES } from "../../../../../../common/types/unifiedMapping";
import DropdownWithSearch from "../../../../../../components/DropdownWithSearch";
import { useAppDispatch } from "../../../../../../store";
import {
  getAllConnections,
  selectConnection,
} from "../../../../connection/connectionSlice";
import { selectCampaign } from "../../../campaignSlice";
import { WIDGET_OPTIONS_DETAILS } from "../constants";
import { isActionDataSame } from "../helpers";
import WidgetContainer from "../WidgetContainer";

const optionDetails = WIDGET_OPTIONS_DETAILS[FLOW_ACTIONS.SALESFORCE];

function SalesforceSyncWidget({
  data: { action, groupId, isCandidate, props, selectedExit, selectedGoto },
}: NodeProps<ActionNodeArgs>) {
  const dispatch = useAppDispatch();
  const {
    unifiedConnection: {
      connectionList: { list, fetchingList },
    },
  } = useSelector(selectConnection);
  const { activeErrorCheck } = useSelector(selectCampaign);
  const [isValid, setIsValid] = useState(true);

  const { saveDraft, setValidity, setActions, readonly } = props;
  const identities = useMemo(() => {
    return {
      actionId: action.action_id,
      groupId,
      branchId: action.branch_id,
    };
  }, [action, groupId]);
  const actionOptions = useMemo(
    () => action.action_options as FlowSalesforceActionOptions,
    [action]
  );

  const setValidityCallback = useCallback(
    (valid: boolean) => {
      setValidity(valid, identities.actionId);
      setIsValid(valid);
    },
    [setValidity, identities.actionId]
  );

  const salesforceConnectionList = useMemo(
    () => list?.filter((val) => val.source === SOURCES.SALESFORCE),
    [list]
  );

  const debouncedSave = useMemo(() => debounce(saveDraft, 500), [saveDraft]);

  const setOptions = useCallback(
    (options: FlowSalesforceActionOptions) => {
      setActions(options, identities.actionId, identities.groupId);
      debouncedSave(options, {
        actionId: identities.actionId,
        groupId: identities.groupId,
      });
    },
    [setActions, identities, debouncedSave]
  );

  const handleOptionChange = useCallback(
    (value: string | null) => {
      const options = {
        connection_id: value,
      };
      setOptions(options);
    },
    [setOptions]
  );

  useEffect(() => {
    dispatch(getAllConnections());
  }, [dispatch]);

  useEffect(() => {
    setValidityCallback(!!actionOptions.connection_id);
  }, [actionOptions.connection_id, setValidityCallback]);

  useEffect(() => {
    if (
      salesforceConnectionList?.length === 1 &&
      !actionOptions.connection_id
    ) {
      handleOptionChange(salesforceConnectionList[0].connection_id);
    }
  }, [
    handleOptionChange,
    salesforceConnectionList,
    actionOptions.connection_id,
  ]);

  return (
    <WidgetContainer
      activeErrorCheck={activeErrorCheck}
      invalidMessage={isValid ? "" : "Salesforce connection is required"}
      identities={identities}
      title={optionDetails.label}
      icon={optionDetails.icon}
      color={optionDetails.color}
      isCandidate={isCandidate}
      selectedExit={selectedExit}
      selectedGoto={selectedGoto}
      isDisabled={readonly}
    >
      <VStack
        alignItems="flex-start"
        marginRight="auto"
        h="100%"
        w="100%"
        px="3"
        py="4"
        pl="30px"
      >
        <FormControl isInvalid={activeErrorCheck && !isValid} pr="10px">
          <DropdownWithSearch
            options={salesforceConnectionList}
            placeholder="Select Connection"
            getOptionLabel={(option) => option?.name as string}
            getOptionValue={(option) => option?.connection_id as string}
            value={salesforceConnectionList?.find(
              (option) => option?.connection_id === actionOptions.connection_id
            )}
            onChange={(option) =>
              handleOptionChange(option?.connection_id ?? null)
            }
            isInvalid={activeErrorCheck && !isValid}
            isLoading={fetchingList}
            maxMenuHeight={300}
            isDisabled={readonly}
            isSearchable
            menuPortalTarget={null}
          />
          <FormErrorMessage>Salesforce connection is required</FormErrorMessage>
        </FormControl>
        <Box width="97%" height="40px">
          <Text fontSize="14px" pt="10px" pl="2px" color="gray.500">
            Trigger a real time sync with Salesforce
          </Text>
        </Box>
      </VStack>
    </WidgetContainer>
  );
}

export default memo(SalesforceSyncWidget, isActionDataSame);
